import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDispensationLogs, addLog } from '../../../ReduxSlices/Prescriptions/dispensationLogSlice';
import LoadingDots from '../../shared/LoadingDots';

const DispensationLog = () => {
  const dispatch = useDispatch();
  const { logs, loading, error, loaded } = useSelector((state) => state.dispensationLogs);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  useEffect(() => {
    if(!loaded){
     dispatch(fetchDispensationLogs());
    }
  }, [dispatch, loaded]);

  const filteredLogs = useMemo(() => {
    const term = searchTerm.toLowerCase();

    const filtered = logs.filter((log) => {
      const patientName = log.patient?.name?.toLowerCase() || '';
      const medNames = log.medications?.map(m => m.name.toLowerCase()).join(', ') || '';
      return patientName.includes(term) || medNames.includes(term);
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (typeof aVal === 'string') {
          return sortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        return sortConfig.direction === 'asc'
          ? new Date(aVal) - new Date(bVal)
          : new Date(bVal) - new Date(aVal);
      });
    }

    return filtered;
  }, [logs, searchTerm, sortConfig]);

  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredLogs.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key && prev.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const getArrow = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const handlePrevious = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNext = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  if (loading) return (<LoadingDots height={10} width={30} color="#38AD3E" />);

  if (error) return <p className="text-red-500">Error: {error}</p>;

  return (
    <div className="flex flex-col min-h-screen mt-4 overflow-hidden">
      <div className="relative mb-4 search">
        <input
          type="text"
          placeholder="Search by patient or medication..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="px-4 py-2 pr-10 border border-gray-300 rounded w-full"
        />
        {searchTerm && (
          <button
            onClick={() => {
              setSearchTerm('');
              setCurrentPage(1);
            }}
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 text-lg font-bold focus:outline-none"
          >
            &times;
          </button>
        )}
      </div>

      <table className="list-table min-w-full border-collapse border border-gray-200 bg-white shadow rounded">
        <thead className="sticky top-0 bg-gray-100 text-gray-700 z-10">
          <tr>
            <th className="px-4 py-2 border">Medications</th>
            <th className="px-4 py-2 border cursor-pointer" onClick={() => handleSort('dispensed_at')}>
              Date
              {getArrow('dispensed_at')}
            </th>
            <th className="px-4 py-2 border">Dispensed By</th>
           
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="5" className="text-center py-4 text-gray-600">
                  <LoadingDots height={10} width={30} color="#38AD3E" />
              </td>
            </tr>
          ) : currentItems.length > 0 ? (
            currentItems.map((log) => (
              <tr key={log.id} className="border-t hover:bg-gray-50">
                <td className="px-4 py-2 border">
                
                    <ul className="med-list text-sm">
                      {log.medications?.length > 0 ? (
                        log.medications.map((item, index) => (
                          <li key={index}>
                            {item.name} — {item.dosage}, {item.frequency}, {item.duration} 
                              {item.quantity && (
                                <span className="text-gray-500"> (Qty: {item.quantity})</span>
                              )}
                          </li>
                        ))
                      ) : (
                        <li className="text-gray-500 italic">No medications found</li>
                      )}
                    </ul>
                </td>
                <td className="px-4 py-2 border text-sm">
                  {new Date(log.dispensed_at).toLocaleString()}
                </td>
                <td className="px-4 py-2 border">{log.pharmacist || 'Unknown'}</td>
               </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5" className="text-center py-4 text-gray-600">
                No matching dispensation logs found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="mt-auto flex justify-between items-center py-4">
        <button
          onClick={handlePrevious}
          type="button"
          disabled={currentPage === 1}
          className="px-4 py-2 bg-gray-200 text-sm rounded disabled:opacity-50"
        >
          Previous
        </button>
        <span className="text-sm text-gray-700">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={handleNext}
          type="button"
          disabled={currentPage === totalPages}
          className="px-4 py-2 bg-gray-200 text-sm rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default DispensationLog;
